import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { saveDepartmentSpendingLineTypes } from 'services/department-spending/departmentSpendingService';

const buildIsFixedByAccountCode = rows =>
  (rows || []).reduce((isFixedByAccountCode, row) => {
    isFixedByAccountCode[row.code] = !!row.isFixed;
    return isFixedByAccountCode;
  }, {});

const DepartmentSpendingFixedSpendingModal = ({
  show,
  onClose,
  onSaved,
  department,
  financialYear,
  rows
}) => {
  const [isFixedByAccountCode, setIsFixedByAccountCode] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) setIsFixedByAccountCode(buildIsFixedByAccountCode(rows));
  }, [show, rows]);

  const handleLineTypeChange = (accountCode, isFixed) => {
    setIsFixedByAccountCode(prev => ({ ...prev, [accountCode]: isFixed }));
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await saveDepartmentSpendingLineTypes({
        department: department?.value,
        financialYearId: financialYear?.value,
        lines: (rows || []).map(row => ({
          accountNumber: row.code,
          isFixed: !!isFixedByAccountCode[row.code]
        }))
      });
      if (res?.success === false) {
        toast.error(res.message);
        return;
      }
      toast.success(res?.message || 'Fixed spending saved successfully.');
      onSaved();
    } catch (error) {
      console.log('AXIOS ERROR', error);
      toast.error('Failed to save fixed spending.');
    } finally {
      setIsSaving(false);
    }
  };

  const rowsSortedByAccountCode = useMemo(
    () =>
      [...(rows || [])].sort((firstRow, secondRow) =>
        String(firstRow.code).localeCompare(String(secondRow.code), undefined, {
          numeric: true
        })
      ),
    [rows]
  );

  const hasLines = !!rowsSortedByAccountCode.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          {department?.label} - {financialYear?.label}
          <div className="fs--1 text-muted">Edit Fixed Spending</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasLines ? (
          rowsSortedByAccountCode.map(row => (
            <div
              key={row.code}
              className="border-bottom"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '0.5rem 0'
              }}
            >
              <span>
                {row.code} - {row.name}
              </span>
              <div style={{ whiteSpace: 'nowrap' }}>
                <Form.Check
                  inline
                  type="radio"
                  id={`department-spending-line-type-${row.code}-fixed`}
                  name={`department-spending-line-type-${row.code}`}
                  label="Fixed"
                  checked={!!isFixedByAccountCode[row.code]}
                  disabled={isSaving}
                  onChange={() => handleLineTypeChange(row.code, true)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id={`department-spending-line-type-${row.code}-variable`}
                  name={`department-spending-line-type-${row.code}`}
                  label="Variable"
                  checked={!isFixedByAccountCode[row.code]}
                  disabled={isSaving}
                  onChange={() => handleLineTypeChange(row.code, false)}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-muted p-4">
            No spending lines available for this department.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || !hasLines}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DepartmentSpendingFixedSpendingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  department: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  financialYear: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string
  }),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      isFixed: PropTypes.bool
    })
  )
};

export default DepartmentSpendingFixedSpendingModal;
